const REFERRAL_TOKEN_KEY = 'referralToken';

const REFERRAL_LAND_PATH_REGEX = /\/users\/referral\/land\/([^/?#]+)/i;

/** Query keys used for referral tokens in landing URLs. */
const REFERRAL_QUERY_KEYS = ['ref', 'referral_token'] as const;

export const normalizeReferralToken = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('u-') ? trimmed.slice(2) : trimmed;
};

export const extractReferralTokenFromPath = (pathname: string): string | null => {
  const match = pathname.match(REFERRAL_LAND_PATH_REGEX);
  if (!match?.[1]) return null;
  return normalizeReferralToken(decodeURIComponent(match[1]));
};

export const extractReferralTokenFromSearch = (search: string): string | null => {
  const params = new URLSearchParams(search);

  for (const key of REFERRAL_QUERY_KEYS) {
    const value = params.get(key);
    if (value?.trim()) {
      return normalizeReferralToken(value);
    }
  }

  return null;
};

export const setReferralToken = (token: string) => {
  const normalized = normalizeReferralToken(token);
  if (!normalized) return;
  sessionStorage.setItem(REFERRAL_TOKEN_KEY, normalized);
};

export const getReferralToken = (): string | null => {
  const stored = sessionStorage.getItem(REFERRAL_TOKEN_KEY);
  return stored ? normalizeReferralToken(stored) : null;
};

export const clearReferralToken = () => {
  sessionStorage.removeItem(REFERRAL_TOKEN_KEY);
};

export const captureReferralTokenFromLocation = (pathname: string, search: string) => {
  const fromPath = extractReferralTokenFromPath(pathname);
  if (fromPath) {
    setReferralToken(fromPath);
    return fromPath;
  }

  const fromQuery = extractReferralTokenFromSearch(search);
  if (fromQuery) {
    setReferralToken(fromQuery);
    return fromQuery;
  }

  return null;
};

/** Remove referral query params from a search string (e.g. after persisting token). */
export const stripReferralParamsFromSearch = (search: string): string => {
  const params = new URLSearchParams(search);
  REFERRAL_QUERY_KEYS.forEach(key => params.delete(key));
  const next = params.toString();
  return next ? `?${next}` : '';
};
